import React, { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_URL;

const Dashboard = () => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // TODAY-FIRST DATA
  const [todayData, setTodayData] = useState(null);

  // SECONDARY DATA
  const [projects, setProjects] = useState([]);
  const [bugs, setBugs] = useState([]);
  const [comments, setComments] = useState([]);

  useEffect(() => {
    const authHeader = {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
    };

    const fetchDashboard = async () => {
      try {
        const todayRes = await axios.get(
          `${API_BASE_URL}/dashboard_today.php`,
          authHeader
        );

        const summaryRes = await axios.get(
          `${API_BASE_URL}/dashboard_summary.php`,
          authHeader
        );

        setTodayData(todayRes.data);

        const { projects, bugs, comments } = summaryRes.data;
        setProjects(projects);
        setBugs(bugs);
        setComments(comments);
      } catch (err) {
        console.error(err);
        setError("Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  if (loading)
    return <div className="text-gray-600">Loading dashboard...</div>;

  if (error)
    return <div className="text-red-600 font-medium">{error}</div>;

  return (
    <>
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <h1 className="text-2xl font-bold">Dashboard</h1>

        <button
          onClick={logout}
          className="bg-red-100 text-red-600 px-4 py-2 rounded-lg hover:bg-red-200 transition"
        >
          Logout
        </button>
      </div>

      {/* TODAY COMMAND CENTER */}
      <TodayCommandCenter stats={todayData.stats} />

      {/* MAIN GRID */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <TodayTasks tasks={todayData.tasks} />
        </div>
        <MessagesToSend messages={todayData.messages} />
      </div>

      {/* SECONDARY DATA */}
      <details className="bg-white p-4 rounded-xl shadow-sm">
        <summary className="cursor-pointer font-semibold">
          📊 Full Overview
        </summary>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-4">
          <Section title="Projects Overview">
            <Table
              headers={["Project", "Status", "Deadline"]}
              rows={projects.map((p) => [
                <Link
                  to={`/projects/${p.id}`}
                  className="text-blue-600 hover:underline"
                >
                  {p.name}
                </Link>,
                p.status,
                p.deadline,
              ])}
            />
          </Section>

          <Section title="Open Bugs">
            <Table
              headers={["Bug", "Priority", "Reported On"]}
              rows={bugs.map((b) => [
                <Link
                  to={`/bugs/${b.id}`}
                  className="text-blue-600 hover:underline"
                >
                  {b.title}
                </Link>,
                b.priority,
                b.createdAt,
              ])}
            />
          </Section>

          <Section title="Recent Comments">
            <ul className="space-y-2">
              {comments.map((c, i) => (
                <li key={i} className="bg-gray-100 p-3 rounded-lg text-sm">
                  <strong>{c.user}:</strong> {c.message}
                </li>
              ))}
            </ul>
          </Section>
        </div>
      </details>

      {/* QUICK ACTIONS */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <ActionButton
          label="➕ Add Project"
          onClick={() => navigate("/projects/add")}
        />
        <ActionButton
          label="🧠 Create Task"
          onClick={() => navigate("/tasks/add")}
        />
        <ActionButton
          label="🐞 Report Bug"
          onClick={() => navigate("/bugs/add")}
        />
      </div>
    </>
  );
};

/* ===================== COMPONENTS ===================== */

const TodayCommandCenter = ({ stats }) => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
    <FocusCard title="🔥 Today’s Focus" items={stats.focus} />
    <FocusCard title="💰 Payments" items={stats.payments} danger />
    <FocusCard title="⚠️ Risks" items={stats.risks} warning />
  </div>
);

const TodayTasks = ({ tasks }) => (
  <Section title="🧠 Today’s Tasks">
    <Table
      headers={["Task", "Project", "Deadline"]}
      rows={tasks.map((t) => [
        <Link to={`/tasks/${t.id}`} className="text-blue-600 hover:underline">
          {t.title}
        </Link>,
        t.project_name,
        <DeadlineBadge date={t.deadline} />,
      ])}
    />
  </Section>
);

const MessagesToSend = ({ messages }) => (
  <Section title="💬 Messages to Send Today">
    <ul className="space-y-3">
      {messages.map((m, i) => (
        <li
          key={i}
          className="flex justify-between items-center bg-gray-50 p-3 rounded-lg"
        >
          <span className="text-sm">{m.label}</span>
          <button
            onClick={() => copyToClipboard(m.prompt)}
            className="bg-blue-600 text-white px-3 py-1.5 rounded-md text-sm hover:bg-blue-700"
          >
            Copy
          </button>
        </li>
      ))}
    </ul>
  </Section>
);

const FocusCard = ({ title, items, danger, warning }) => (
  <div
    className={`p-4 rounded-xl border shadow-sm ${
      danger
        ? "bg-red-50 border-red-200"
        : warning
        ? "bg-yellow-50 border-yellow-200"
        : "bg-white"
    }`}
  >
    <h3 className="font-semibold mb-2">{title}</h3>
    <ul className="text-sm space-y-1">
      {items.length === 0 ? (
        <li className="text-gray-500">Nothing urgent 🎉</li>
      ) : (
        items.map((i, idx) => <li key={idx}>• {i}</li>)
      )}
    </ul>
  </div>
);

const DeadlineBadge = ({ date }) => {
  const isOverdue = new Date(date) < new Date();
  return (
    <span
      className={`px-2 py-1 rounded text-xs ${
        isOverdue
          ? "bg-red-100 text-red-700"
          : "bg-green-100 text-green-700"
      }`}
    >
      {date}
    </span>
  );
};

const ActionButton = ({ label, onClick }) => (
  <button
    onClick={onClick}
    className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition"
  >
    {label}
  </button>
);

const Section = ({ title, children }) => (
  <div className="bg-white p-4 rounded-xl shadow-sm">
    <h2 className="font-semibold mb-4">{title}</h2>
    {children}
  </div>
);

const Table = ({ headers, rows }) => (
  <div className="overflow-x-auto -mx-4 md:mx-0">
    <table className="min-w-full text-sm">
      <thead className="bg-gray-50 sticky top-0">
        <tr>
          {headers.map((h, i) => (
            <th key={i} className="text-left px-3 py-2 font-semibold">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row, i) => (
          <tr key={i} className="hover:bg-gray-50">
            {row.map((cell, j) => (
              <td key={j} className="px-3 py-2 border-t">
                {cell}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const copyToClipboard = (text) => {
  navigator.clipboard.writeText(text);
};

export default Dashboard;
